/**
 * Gateway switcher — shows all configured gateways with status.
 *
 * Appears in the settings panel. Allows selecting the active gateway,
 * removing gateways (with confirmation), and adding new ones.
 *
 * Status indicators:
 * - 🟢 Connected
 * - 🟡 Connecting
 * - ⏳ Pending (waiting for pairing approval)
 * - 🔴 Error
 * - ⚪ Disconnected / Paired / New
 */

import { useState, useCallback } from 'react';
import type { GatewayConfig, GatewayStatus } from '../types/gateway';
import styles from './GatewayList.module.css';

interface GatewayListProps {
  /** All configured gateways */
  gateways: GatewayConfig[];
  /** Currently active gateway ID */
  activeGatewayId: string | null;
  /** Live connection states keyed by gateway ID */
  connectionStates: Map<string, GatewayStatus>;
  /** Called when a gateway is selected (switch active) */
  onSelect: (id: string) => void;
  /** Called when a gateway should be removed */
  onRemove: (id: string) => void;
  /** Called when the user wants to add a new gateway */
  onAdd: () => void;
}

/** Status emoji mapping */
const STATUS_ICONS: Record<GatewayStatus, string> = {
  connected: '🟢',
  connecting: '🟡',
  pending: '⏳',
  error: '🔴',
  origin_blocked: '🚫',
  paired: '⚪',
  disconnected: '⚪',
  new: '⚪',
};

/** Status label mapping */
const STATUS_LABELS: Record<GatewayStatus, string> = {
  connected: 'Connected',
  connecting: 'Connecting…',
  pending: 'Awaiting approval',
  error: 'Error',
  origin_blocked: 'Origin blocked',
  paired: 'Paired',
  disconnected: 'Disconnected',
  new: 'New',
};

/**
 * Gateway list/switcher component.
 *
 * Compact enough to fit in a settings sidebar or dropdown.
 */
export function GatewayList({
  gateways,
  activeGatewayId,
  connectionStates,
  onSelect,
  onRemove,
  onAdd,
}: GatewayListProps) {
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  /** Handle remove with confirmation */
  const handleRemove = useCallback(
    (id: string) => {
      if (confirmRemoveId === id) {
        // Second click — confirm removal
        onRemove(id);
        setConfirmRemoveId(null);
      } else {
        // First click — ask for confirmation
        setConfirmRemoveId(id);
        // Auto-reset after 3 seconds
        setTimeout(() => setConfirmRemoveId(null), 3000);
      }
    },
    [confirmRemoveId, onRemove],
  );

  if (gateways.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>No gateways configured</div>
        <button className={styles.addBtn} onClick={onAdd}>
          + Add Gateway
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {gateways.map((gw) => {
        const status: GatewayStatus = connectionStates.get(gw.id) || gw.status || 'disconnected';
        const isActive = gw.id === activeGatewayId;
        const isConfirming = confirmRemoveId === gw.id;

        return (
          <div
            key={gw.id}
            className={`${styles.item} ${isActive ? styles.active : ''}`}
            onClick={() => onSelect(gw.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSelect(gw.id)}
            title={STATUS_LABELS[status]}
          >
            <span className={styles.status}>
              {STATUS_ICONS[status] || '⚪'}
            </span>
            <div className={styles.info}>
              <div className={styles.name}>{gw.name || 'Gateway'}</div>
              <div className={styles.url}>{gw.url}</div>
            </div>
            <div className={styles.actions}>
              <button
                className={`${styles.removeBtn} ${isConfirming ? styles.confirmRemove : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(gw.id);
                }}
                title={isConfirming ? 'Click again to confirm' : 'Remove'}
              >
                {isConfirming ? '⚠️' : '✕'}
              </button>
            </div>
          </div>
        );
      })}
      <button className={styles.addBtn} onClick={onAdd}>
        + Add Gateway
      </button>
    </div>
  );
}
